import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { Action } from '@ngrx/store';
import { catchError, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { bexioActions } from '../actions/bexio.actions';
import { BexioService } from '../services/bexio.service';
import { AumicoCoreProvider, NotificationType } from '@modeso/aumico-lib-core-fe';

@Injectable()
export class BexioEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly bexioService: BexioService,
        private readonly coreProvider: AumicoCoreProvider,
    ) {}

    loadConnection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(bexioActions.loadConnection),
            switchMap(({ companyId }) =>
                this.bexioService.getConnection(companyId).pipe(
                    map((connection) => bexioActions.loadConnectionSuccess({ connection })),
                    catchError((error: HttpErrorResponse) => of(bexioActions.loadConnectionFailure({ error }))),
                ),
            ),
        ),
    );

    loadBusinessYears$ = createEffect(() =>
        this.actions$.pipe(
            ofType(bexioActions.loadBusinessYears),
            switchMap(({ companyId }) =>
                this.bexioService.getBusinessYears(companyId).pipe(
                    map((businessYears) => bexioActions.loadBusinessYearsSuccess({ businessYears })),
                    catchError((error: HttpErrorResponse) =>
                        of(bexioActions.loadBusinessYearsFailure({ error })),
                    ),
                ),
            ),
        ),
    );

    connect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(bexioActions.connect),
            mergeMap(({ companyId }) =>
                this.bexioService.getConnectUrl(companyId).pipe(
                    mergeMap(({ authUrl }) =>
                        new Observable<Action>((observer) => {
                            const popup = window.open(authUrl, 'bexio-auth', 'width=600,height=700');
                            let done = false;

                            let pollTimer: ReturnType<typeof setTimeout>;

                            const finish = (action: Action) => {
                                if (done) return;
                                done = true;
                                clearTimeout(pollTimer);
                                channel.close();
                                popup?.close();
                                observer.next(action);
                                observer.complete();
                            };

                            // BroadcastChannel handles error fast-path and works as a success
                            // signal when FE and BE share the same origin (e.g. production LB).
                            const channel = new BroadcastChannel('bexio-auth');
                            channel.onmessage = (event: MessageEvent) => {
                                if (event.data?.type === 'bexio-auth-success') {
                                    finish(bexioActions.connectSuccess({ companyId }));
                                } else if (event.data?.type === 'bexio-auth-error') {
                                    finish(
                                        bexioActions.connectFailure({
                                            error: new HttpErrorResponse({ error: event.data.message }),
                                        }),
                                    );
                                }
                            };

                            // Poll the connection API — works across origins and survives COOP.
                            // Backoff: every 1s for the first 30s, then every 2s up to 2 min total.
                            // 30×1s + 45×2s = 120s. The BE saves the connection before serving
                            // the success HTML, so a connected status means the flow completed.
                            const FAST_POLLS = 30;
                            const TOTAL_POLLS = 75; // 30 + 45
                            let polls = 0;

                            const schedulePoll = () => {
                                if (done) return;
                                if (polls >= TOTAL_POLLS) {
                                    finish(bexioActions.connectFailure({
                                        error: new HttpErrorResponse({ error: 'Connection timeout' }),
                                    }));
                                    return;
                                }
                                const delay = polls < FAST_POLLS ? 1000 : 2000;
                                pollTimer = setTimeout(() => {
                                    polls++;
                                    this.bexioService.getConnection(companyId).subscribe({
                                        next: (conn) => {
                                            if (conn?.status === 'connected') {
                                                finish(bexioActions.connectSuccess({ companyId }));
                                            } else {
                                                schedulePoll();
                                            }
                                        },
                                        error: () => schedulePoll(),
                                    });
                                }, delay);
                            };
                            schedulePoll();

                            return () => {
                                done = true;
                                clearTimeout(pollTimer);
                                channel.close();
                            };
                        }).pipe(
                            takeUntil(this.actions$.pipe(ofType(bexioActions.cancelConnect))),
                        ),
                    ),
                    catchError((error: HttpErrorResponse) => of(bexioActions.connectFailure({ error }))),
                ),
            ),
        ),
    );

    disconnect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(bexioActions.disconnect),
            switchMap(({ companyId }) =>
                this.bexioService.disconnect(companyId).pipe(
                    map(() => bexioActions.disconnectSuccess()),
                    catchError((error: HttpErrorResponse) => of(bexioActions.disconnectFailure({ error }))),
                ),
            ),
        ),
    );

    connectSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(bexioActions.connectSuccess),
            tap(() =>
                this.coreProvider.dispatchAddNotification(
                    'aumico_bexio_connect_success_title',
                    'aumico_bexio_connect_success_description',
                    NotificationType.SUCCESS,
                ),
            ),
            map(({ companyId }) => bexioActions.loadConnection({ companyId })),
        ),
    );

    disconnectSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(bexioActions.disconnectSuccess),
                tap(() =>
                    this.coreProvider.dispatchAddNotification(
                        'aumico_bexio_disconnect_success_title',
                        'aumico_bexio_disconnect_success_description',
                        NotificationType.SUCCESS,
                    ),
                ),
            ),
        { dispatch: false },
    );

    connectFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(bexioActions.connectFailure),
                tap(() =>
                    this.coreProvider.dispatchAddNotification(
                        'aumico_bexio_connect_failure_title',
                        'aumico_bexio_connect_failure_description',
                        NotificationType.ERROR,
                    ),
                ),
            ),
        { dispatch: false },
    );

    disconnectFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(bexioActions.disconnectFailure),
                tap(() =>
                    this.coreProvider.dispatchAddNotification(
                        'aumico_bexio_disconnect_failure_title',
                        'aumico_bexio_disconnect_failure_description',
                        NotificationType.ERROR,
                    ),
                ),
            ),
        { dispatch: false },
    );

    importData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(bexioActions.importData),
            switchMap(({ companyId, annualReportId, businessYearId, previousBusinessYearId, annualReportMetaInfoId }) =>
                this.bexioService.importData({ companyId, annualReportId, businessYearId, previousBusinessYearId, annualReportMetaInfoId }).pipe(
                    map((result) => bexioActions.importDataSuccess({ result })),
                    catchError((error: HttpErrorResponse) => of(bexioActions.importDataFailure({ error }))),
                ),
            ),
        ),
    );
}
